import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wifi, Smartphone, Zap, Play, Check, Shield, Tv, Headphones, Cable, Mail, ArrowRight, Radio } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';
import BravoPricingSection from '../components/BravoPricingSection';

// User provided images
const HERO_IMAGE = "https://images.unsplash.com/photo-1749900075879-5b8fd33be4f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjaW5lbWElMjB0aGVhdGVyJTIwaW50ZXJpb3IlMjBnb2xkJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwOTA2NjU5fDA&ixlib=rb-4.1.0&q=85";
const CINEMA_IMAGE = "https://images.unsplash.com/photo-1761502479994-3a5e07ec243e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBwcm9qZWN0aW9uJTIwcm9vbSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTA2NzI1fDA&ixlib=rb-4.1.0&q=85";

// Bravo device images provided by user
const BRAVO_DEVICE_ORBITAL = "https://customer-assets.emergentagent.com/job_07253317-1e1c-471e-bbd1-023abce1b255/artifacts/1bivw15k_imagen_bravo_01.jpg";
const BRAVO_DEVICE_WITH_PHONE = "https://customer-assets.emergentagent.com/job_07253317-1e1c-471e-bbd1-023abce1b255/artifacts/2u7z4rpv_imagen_bravo_02.jpg";

// Custom hook for scroll-triggered fade-in animations
const useScrollFadeIn = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Section wrapper component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollFadeIn();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

const DispositivoBravoPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header onOpenAuth={() => setShowAuth(true)} onOpenSearch={() => setShowSearch(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-end">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Teatro de lujo"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 to-transparent" />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#e4002b] rounded flex items-center justify-center">
                <Play className="w-6 h-6 text-white fill-white" />
              </div>
              <span className="text-[#e4002b] font-bold text-lg tracking-widest uppercase">Dispositivo Bravo</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-none mb-6" style={{ fontFamily: '"Oswald", sans-serif' }}>
              EL FUTURO DE LA EXHIBICIÓN
              <span className="block text-[#e4002b]">ES DIGITAL</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 font-light mb-4">
              Es ágil y está en tus manos.
            </p>
            
            <p className="text-lg text-white/60 leading-relaxed mb-10 max-w-xl">
              ¿Tu sala está preparada para lo que el público exige hoy? El cine ha dejado de ser solo películas. 
              El espectador actual busca experiencias: la emoción de un directo desde Londres, la energía de un concierto de K-Pop 
              o la majestuosidad de la ópera.
            </p>
            
            <a 
              href="mailto:m.cazorla@versiondigital.es?subject=Solicitud Demo Dispositivo Bravo"
              className="inline-flex items-center gap-3 bg-[#e4002b] text-white px-8 py-4 font-bold tracking-wider uppercase hover:bg-[#c70024] transition-all hover:scale-105 shadow-[0_0_30px_rgba(228,0,43,0.4)]"
              data-testid="hero-cta-btn"
            >
              Solicitar Demo
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Problem Statement - No Image */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(228,0,43,0.08)_0%,transparent_60%)]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">El problema</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8" style={{ fontFamily: '"Oswald", sans-serif' }}>
              HASTA AHORA, EL SATÉLITE ERA UNA BARRERA COSTOSA Y COMPLEJA
            </h2>
            <p className="text-xl text-white/70 leading-relaxed mb-6">
              <span className="text-[#e4002b] font-bold">Bravo rompe las reglas.</span> Con Bravo, transformas tu sala en un escenario global 
              utilizando únicamente tu conexión a internet.
            </p>
            <p className="text-lg text-white/50">
              Control total desde tu smartphone. Sin instalaciones complejas. Sin dependencias técnicas.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Features Bento Grid - No Image in Large Card */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Características</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: '"Oswald", sans-serif' }}>
              POTENCIA Y CONTROL
            </h2>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 - Large without background image */}
            <AnimatedSection className="md:col-span-2 md:row-span-2" delay={100}>
              <div className="h-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 group hover:border-[#e4002b]/50 transition-all duration-500 relative overflow-hidden p-8 md:p-12 flex flex-col justify-center">
                {/* Decorative gradient background */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(228,0,43,0.15)_0%,transparent_60%)]" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#e4002b]/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#e4002b]/30 transition-colors">
                    <Wifi className="w-8 h-8 text-[#e4002b]" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Oswald", sans-serif' }}>
                    LIBERTAD TOTAL: ADIÓS AL SATÉLITE
                  </h3>
                  <p className="text-white/70 text-lg leading-relaxed max-w-lg mb-6">
                    Proyecta contenido en directo con la máxima estabilidad y calidad 4K sin instalaciones complejas. 
                    Bravo utiliza tecnología de streaming de última generación optimizada para entornos de cine.
                  </p>
                  <p className="text-[#e4002b] font-bold text-xl">
                    Si tienes internet, tienes un evento mundial.
                  </p>
                </div>
              </div>
            </AnimatedSection>
            
            {/* Feature 2 */}
            <AnimatedSection delay={200}>
              <div className="h-full bg-[#1a1a1a] border border-white/10 p-8 group hover:border-[#e4002b]/50 transition-all duration-500">
                <div className="w-12 h-12 bg-[#e4002b]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#e4002b]/20 transition-colors">
                  <Smartphone className="w-6 h-6 text-[#e4002b]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: '"Oswald", sans-serif' }}>
                  CONTROL ABSOLUTO DESDE TU BOLSILLO
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Gestiona la proyección, controla el audio y monitoriza el evento desde tu smartphone escaneando un código QR.
                </p>
              </div>
            </AnimatedSection>
            
            {/* Feature 3 */}
            <AnimatedSection delay={300}>
              <div className="h-full bg-[#1a1a1a] border border-white/10 p-8 group hover:border-[#e4002b]/50 transition-all duration-500">
                <div className="w-12 h-12 bg-[#e4002b]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#e4002b]/20 transition-colors">
                  <Zap className="w-6 h-6 text-[#e4002b]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: '"Oswald", sans-serif' }}>
                  TÚ TIENES EL MANDO
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Olvídate de procesos técnicos tediosos. Interfaz intuitiva para que tú tengas el control, Bravo hace el resto.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Technology Section with Image */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <AnimatedSection className="relative" delay={100}>
              <div className="absolute inset-0 bg-[#e4002b]/20 blur-3xl transform scale-75" />
              <img 
                src={CINEMA_IMAGE}
                alt="Sala de cine moderna"
                className="relative z-10 w-full rounded-lg border border-white/10"
              />
            </AnimatedSection>
            
            {/* Content */}
            <AnimatedSection delay={200}>
              <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Tecnología</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Oswald", sans-serif' }}>
                TECNOLOGÍA INVISIBLE, IMPACTO GIGANTE
              </h2>
              <p className="text-xl text-white/70 mb-8">
                Bravo es un dispositivo compacto y potente que se integra perfectamente en tu cabina de proyección.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#e4002b]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-[#e4002b]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Instalación instantánea</h4>
                    <p className="text-white/60">Conexión HDMI y red. Nada más.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#e4002b]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#e4002b]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Fiabilidad extrema</h4>
                    <p className="text-white/60">Diseñado para evitar cortes y garantizar una experiencia cinematográfica perfecta.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#e4002b]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Play className="w-5 h-5 text-[#e4002b]" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Catálogo Exclusivo</h4>
                    <p className="text-white/60">Acceso directo a temporadas de la Royal Opera House, conciertos internacionales y los grandes hitos del arte mundial.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-24 md:py-32 bg-[#0f0f0f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'repeating-linear-gradient(90deg, #e4002b 0, #e4002b 1px, transparent 1px, transparent 100px), repeating-linear-gradient(0deg, #e4002b 0, #e4002b 1px, transparent 1px, transparent 100px)'
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Especificaciones</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: '"Oswald", sans-serif' }}>
              DETALLES TÉCNICOS
            </h2>
          </AnimatedSection>
          
          <AnimatedSection delay={150}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
              <div className="bg-[#0f0f0f] p-8 text-center group hover:bg-[#1a1a1a] transition-colors">
                <Tv className="w-8 h-8 text-[#e4002b] mx-auto mb-4" />
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Resolución</p>
                <p className="text-white text-xl font-bold">4K Ultra HD</p>
              </div>
              <div className="bg-[#0f0f0f] p-8 text-center group hover:bg-[#1a1a1a] transition-colors">
                <Headphones className="w-8 h-8 text-[#e4002b] mx-auto mb-4" />
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Audio</p>
                <p className="text-white text-xl font-bold">5.1</p>
              </div>
              <div className="bg-[#0f0f0f] p-8 text-center group hover:bg-[#1a1a1a] transition-colors">
                <Radio className="w-8 h-8 text-[#e4002b] mx-auto mb-4" />
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Reproducción</p>
                <p className="text-white text-xl font-bold">Live + Encore + Rec</p>
              </div>
              <div className="bg-[#0f0f0f] p-8 text-center group hover:bg-[#1a1a1a] transition-colors">
                <Cable className="w-8 h-8 text-[#e4002b] mx-auto mb-4" />
                <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Conectividad</p>
                <p className="text-white text-xl font-bold">HDMI + Ethernet</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing Section */}
      <BravoPricingSection />

      {/* Final CTA */}
      <section className="py-32 md:py-40 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(228,0,43,0.15)_0%,transparent_60%)]" />
        
        <AnimatedSection className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: '"Oswald", sans-serif' }}>
            NO TE QUEDES FUERA DE LA
            <span className="block text-[#e4002b]">NUEVA ERA DEL CINE</span>
          </h2>
          
          <p className="text-xl text-white/70 mb-6 max-w-2xl mx-auto">
            Las salas que innovan son las que lideran. Convierte tu cine en un centro cultural de vanguardia 
            y multiplica tu box office con la solución más eficiente del mercado.
          </p>
          
          <p className="text-lg text-white/50 mb-10">
            Solicita tu demo hoy mismo y descubre por qué el sector ya dice <span className="text-[#e4002b] font-bold">¡Bravo!</span>
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:m.cazorla@versiondigital.es?subject=Solicitud Demo Dispositivo Bravo"
              className="inline-flex items-center gap-3 bg-[#e4002b] text-white px-10 py-5 font-bold tracking-wider uppercase hover:bg-[#c70024] transition-all hover:scale-105 shadow-[0_0_40px_rgba(228,0,43,0.5)]"
              data-testid="cta-demo-btn"
            >
              <Mail className="w-5 h-5" />
              Solicitar Demo
            </a>
            
            <Link 
              to="/cinema/register"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-10 py-5 font-bold tracking-wider uppercase hover:bg-white/10 transition-all"
              data-testid="cta-registro-btn"
            >
              Registrar mi cine
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </AnimatedSection>
      </section>

      <Footer />
      
      {/* Modals */}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </div>
  );
};

export default DispositivoBravoPage;
